<template>
  <div class="enquete-fs-wrapper" :key="item.id">
    <!-- SVG background as inline element for proper fullscreen coverage -->
    <div class="enquete-fs-bg">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient
            id="bg-gradient"
            x1="4.1"
            y1="1090.69"
            x2="1890.31"
            y2="1.68"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stop-color="#fe5169" />
            <stop offset=".49" stop-color="#fb7b91" />
            <stop offset=".85" stop-color="#f99eb2" />
          </linearGradient>
          <linearGradient
            id="left-gradient"
            x1="180.67"
            y1="1442.92"
            x2="-734.54"
            y2="1442.92"
            gradientTransform="translate(-1208.82 706.33) rotate(90) scale(1 -1)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stop-color="#fdee4d" />
            <stop offset=".06" stop-color="#fcec57" />
            <stop offset=".52" stop-color="#f9deae" />
            <stop offset=".84" stop-color="#f6d6e4" />
            <stop offset="1" stop-color="#f6d3fa" />
          </linearGradient>
          <linearGradient
            id="right-gradient"
            x1="1821.01"
            y1="1080"
            x2="1821.01"
            y2="583.69"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stop-color="#fef023" />
            <stop offset=".57" stop-color="#f9dfa0" />
            <stop offset="1" stop-color="#f6d3fa" />
          </linearGradient>
        </defs>
        <!-- Main pink/coral gradient background -->
        <rect fill="url(#bg-gradient)" x="0" y="0" width="1920" height="1080" />
        <!-- Left decorative polygon (yellow/pink gradient) -->
        <polygon
          fill="url(#left-gradient)"
          points="-56.14 -28.21 -56.14 887 48.96 885 48.96 608 199.49 604.76 199.96 333.14 360.43 333.14 361.12 49.56 524.33 49.56 524.33 -28.21 -56.14 -28.21"
        />
        <!-- Right decorative polygon (yellow gradient) -->
        <polygon
          fill="url(#right-gradient)"
          points="1947.09 583.69 1835.52 583.69 1835.52 873.96 1694.93 873.96 1694.93 1080 1926.18 1080 1926.18 896 1947.09 896 1947.09 583.69"
        />
      </svg>
    </div>

    <!-- Content container - centers poll-card -->
    <div class="content-center">
      <div class="poll-card">
        <!-- Accent gradient strip — left edge aligned with title -->
        <div class="poll-accent" />

        <!-- Teal question title block -->
        <div class="poll-title" v-if="!hideTitle">
          <span class="poll-title-text" :style="titleTextStyle">
            {{ item.dsc_titulo_enquete }}
          </span>
        </div>

        <!-- Options list -->
        <div class="poll-options" :style="optionsListStyle">
          <div
            v-for="(opcao, index) in item.enquete_opcoes"
            :key="opcao.id"
            class="poll-option"
            :style="optionRowStyle"
          >
            <!-- Badge: image or letter -->
            <div class="fs-badge" :style="badgeSizeStyle">
              <img
                v-if="opcao.url_imagem_enquete"
                :src="opcao.url_imagem_enquete"
                :alt="letters[index]"
              />
              <span v-else class="fs-badge-letter" :style="badgeLetterStyle">{{
                letters[index]
              }}</span>
            </div>

            <!-- Label + bar -->
            <div class="fs-data" :style="{ gap: lay.rowGap / 4 + 'px' }">
              <span class="fs-label" :style="labelStyle" v-if="!hideOptionTitle">{{ opcao.dsc_titulo }}</span>

              <div class="fs-bar-row" :style="barRowStyle">
                <div class="fs-bar-teal" :style="tealFillStyle(index)">
                  <span
                    v-if="item.flg_exibir_resultado"
                    class="fs-bar-pct"
                    :style="pctTextStyle"
                    >{{ animatedPercentages[index] || 0 }} %</span
                  >
                </div>
                <div class="fs-bar-red" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Animation state — outside Vue, never reactive
const anim = { frames: {}, targets: {} };

// poll-card width = 1400, badge = 90, gap = 24  →  bar = 1286
const CARD_W = 1400;
const BADGE_SZ = 90;
const ROW_GAP = 24;
const BAR_W = CARD_W - BADGE_SZ - ROW_GAP;

// Per-option-count adaptive layout
const LAYOUT = {
  2: { rowH: 160, rowGap: 24, marginTop: 70, barH: 80, bfs: 52, lfs: 30, pfs: 34 },
  3: { rowH: 130, rowGap: 20, marginTop: 60, barH: 66, bfs: 46, lfs: 27, pfs: 30 },
  4: { rowH: 110, rowGap: 16, marginTop: 55, barH: 56, bfs: 42, lfs: 25, pfs: 28 },
  5: { rowH: 94, rowGap: 14, marginTop: 48, barH: 48, bfs: 38, lfs: 23, pfs: 25 },
  6: { rowH: 80, rowGap: 12, marginTop: 42, barH: 42, bfs: 34, lfs: 21, pfs: 22 },
};

export default {
  name: "EnqueteTarjaFullScreen",

  props: {
    item: { type: Object, required: true },
    hideTitle: { type: Boolean, default: false },
    hideOptionTitle: { type: Boolean, default: false },
  },

  data() {
    return {
      letters: ["A", "B", "C", "D", "E", "F"],
      fonts: { title: "Gobold Regular", label: "Gobold Thin" },
      animationDuration: 1800,
      animatedPercentages: [],
      previousResultsVisible: false,
    };
  },

  computed: {
    n() {
      return this.item?.enquete_opcoes?.length || 0;
    },
    lay() {
      return LAYOUT[this.n] || LAYOUT[6];
    },

    optionsListStyle() {
      return { marginTop: this.lay.marginTop + "px", gap: this.lay.rowGap + "px" };
    },
    optionRowStyle() {
      return { height: this.lay.rowH + "px", gap: ROW_GAP + "px" };
    },
    badgeSizeStyle() {
      return {
        width: BADGE_SZ + "px",
        height: BADGE_SZ + "px",
        minWidth: BADGE_SZ + "px",
      };
    },
    badgeLetterStyle() {
      return {
        fontFamily: `${this.fonts.title} !important`,
        fontSize: this.lay.bfs + "px",
      };
    },
    titleTextStyle() {
      return { fontFamily: `${this.fonts.title} !important` };
    },
    labelStyle() {
      return {
        fontFamily: `${this.fonts.label} !important`,
        fontSize: this.lay.lfs + "px",
      };
    },
    barRowStyle() {
      return { height: this.lay.barH + "px" };
    },
    pctTextStyle() {
      return {
        fontFamily: `${this.fonts.title} !important`,
        fontSize: this.lay.pfs + "px",
        paddingLeft: "14px",
      };
    },
  },

  methods: {
    tealFillStyle(index) {
      const pct = this.item?.flg_exibir_resultado
        ? this.animatedPercentages[index] || 0
        : 0;
      const fillW = Math.round((pct / 100) * BAR_W);
      return { width: fillW + "px", height: "100%" };
    },

    animateTo(index, from, to) {
      const key = `p${index}`;
      if (anim.frames[key]) {
        cancelAnimationFrame(anim.frames[key]);
        delete anim.frames[key];
      }
      anim.targets[key] = to;
      if (from === to) {
        this.$set(this.animatedPercentages, index, to);
        return;
      }
      const dur = this.animationDuration,
        start = performance.now(),
        diff = to - from;
      const tick = (now) => {
        if (anim.targets[key] !== to) return;
        const p = Math.min((now - start) / dur, 1);
        this.$set(
          this.animatedPercentages,
          index,
          Math.round(from + diff * (1 - Math.pow(1 - p, 4)))
        );
        if (p < 1) anim.frames[key] = requestAnimationFrame(tick);
        else {
          this.$set(this.animatedPercentages, index, to);
          delete anim.frames[key];
        }
      };
      anim.frames[key] = requestAnimationFrame(tick);
    },

    initPercentages(animate = false) {
      (this.item?.enquete_opcoes || []).forEach((opcao, i) => {
        const target = opcao.resultado || 0;
        if (animate) {
          this.$set(this.animatedPercentages, i, 0);
          setTimeout(() => this.animateTo(i, 0, target), 150 + i * 200);
        } else {
          this.$set(this.animatedPercentages, i, target);
        }
      });
    },

    stopAll() {
      Object.keys(anim.frames).forEach((k) => {
        cancelAnimationFrame(anim.frames[k]);
        delete anim.frames[k];
      });
      Object.keys(anim.targets).forEach((k) => delete anim.targets[k]);
    },

    resetPercentages() {
      this.stopAll();
      this.animatedPercentages = [];
    },
  },

  mounted() {
    if (this.item?.flg_exibir_resultado) {
      this.initPercentages(false);
      this.previousResultsVisible = true;
    } else this.resetPercentages();
  },

  beforeDestroy() {
    this.stopAll();
  },

  watch: {
    "item.flg_exibir_resultado": {
      handler(val) {
        if (val && !this.previousResultsVisible) this.initPercentages(true);
        else if (val && this.previousResultsVisible) this.initPercentages(false);
        else if (!val) this.resetPercentages();
        this.previousResultsVisible = !!val;
      },
      immediate: true,
    },
    "item.enquete_opcoes": {
      deep: true,
      handler(opcoes) {
        if (!this.item?.flg_exibir_resultado || !this.previousResultsVisible) return;
        opcoes.forEach((o, i) => {
          const t = o.resultado || 0;
          if (anim.targets[`p${i}`] !== t)
            this.animateTo(i, this.animatedPercentages[i] || 0, t);
        });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Gobold Regular";
  src: url("@/assets/Gobold_Regular.otf") format("opentype");
}
@font-face {
  font-family: "Gobold Thin";
  src: url("@/assets/Gobold_Thin.otf") format("opentype");
}

/* Root: full 1920×1080 canvas - FIXED positioning */
.enquete-fs-wrapper {
  position: relative;
  top: 0;
  left: 0;
  width: 1920px;
  height: 1080px;
  overflow: hidden;
  pointer-events: none;
}

/* SVG background - covers entire wrapper */
.enquete-fs-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
}

/* Flexbox container — centres poll-card both axes */
.content-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

/* Poll card column */
.poll-card {
  display: flex;
  flex-direction: column;
  width: 1400px;
}

/* Accent gradient — left-aligned with title, shorter on right */
.poll-accent {
  width: 1118px;
  height: 60px;
  flex-shrink: 0;
  background: linear-gradient(90deg, #fe810a 0%, #bcf6ef 100%);
}

/* Title block */
.poll-title {
  width: 1400px;
  min-height: 163px;
  background: #00a493;
  padding: 24px 40px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;
}

.poll-title-text {
  color: #fff;
  font-size: 56px;
  font-weight: 900;
  text-transform: uppercase;
  line-height: 1.18;
  letter-spacing: 0.02em;
}

/* Options list */
.poll-options {
  display: flex;
  flex-direction: column;
  width: 1400px;
  flex-shrink: 0;
}

/* Single option row */
.poll-option {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 1400px;
  flex-shrink: 0;
}

/* Badge */
.fs-badge {
  background: #00a493;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

/* Image fills badge square */
.fs-badge img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Letter in badge */
.fs-badge-letter {
  color: #fff;
  font-weight: 900;
  line-height: 1;
}

/* Data column */
.fs-data {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.fs-label {
  display: block;
  color: #fff;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Bar row */
.fs-bar-row {
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.fs-bar-teal {
  height: 100%;
  background: #00a493;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  transition: width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fs-bar-pct {
  color: #fff;
  font-weight: 900;
  line-height: 1;
  white-space: nowrap;
}

.fs-bar-red {
  flex: 1;
  height: 100%;
  background: #fe676a;
}
</style>
